/**
 * FmFormFlowPage — descriptor-only form and settings archetype.
 *
 * The renderer owns linear progress, non-linear section navigation, fields,
 * repeatable rows, maps, evidence, tables, terminal actions, focus movement,
 * file status, and one-time-secret containment. Routes provide one exact,
 * data-only descriptor and receive typed domain events.
 */
/* eslint-disable react-refresh/only-export-components -- canonical entrypoint exports contract and validator. */
import React from 'react';
import { FmButton } from './FmButton';
import { FmConfirm } from './FmConfirm';
import { FmInline, FmRoutePage, FmText } from './FmComposition';
import {
  FlowHeader,
  LinearNavigation,
  LinearStageStateSurface,
  LinearStageStory,
  SectionNavigation,
  SectionStateSurface,
  StateSurface,
} from './FmFormFlowChrome';
import { FmFormFlowBlocks, ActiveFlowHeading, Values } from './FmFormFlowContent';
import { FmContentWidth } from './FmLayout';
import { StoryTransitionFrame } from './internal/StoryTransitionFrame';
import {
  activeBlocks,
  activeItem,
  activeSectionItem,
  sectionStateAllowsInteraction,
  stageAllowsControls,
  stageNoticeBlockId,
  stateSurfaceActionIds,
} from './FmFormFlowPage.helpers';
import type {
  FmFormFlowLinearItemDescriptor,
  FmFormFlowPageDescriptor,
  FmFormFlowPageProps,
  FmFormFlowSectionItemDescriptor,
  FmFormFlowStageState,
} from './FmFormFlowPage.types';
import { assertFmFormFlowPageDescriptor } from './FmFormFlowPage.validation';
import './FmFormFlowPage.css';

export type {
  FmFormFlowActionDescriptor,
  FmFormFlowBlockDescriptor,
  FmFormFlowEditableCellEditorDescriptor,
  FmFormFlowEditableCellOptionDescriptor,
  FmFormFlowEditableColumnDescriptor,
  FmFormFlowEvent,
  FmFormFlowFieldDescriptor,
  FmFormFlowFileDescriptor,
  FmFormFlowItemState,
  FmFormFlowLinearItemDescriptor,
  FmFormFlowNavigationDescriptor,
  FmFormFlowPageBase,
  FmFormFlowPageDescriptor,
  FmFormFlowPageProps,
  FmFormFlowRecordDescriptor,
  FmFormFlowScalar,
  FmFormFlowSectionItemDescriptor,
  FmFormFlowStageState,
  FmFormFlowStageStateCopy,
  FmFormFlowStageStoryDescriptor,
  FmFormFlowState,
  FmFormFlowSummaryDescriptor,
  FmFormFlowTableColumnDescriptor,
  FmFormFlowTableRowDescriptor,
  FmFormFlowValidationDescriptor,
  FmFormFlowValueDescriptor,
} from './FmFormFlowPage.types';
export { assertFmFormFlowPageDescriptor } from './FmFormFlowPage.validation';
export {
  activeFormFlowStageState,
  formFlowStageStatus,
  haveExactUniqueFormFlowIds,
} from './FmFormFlowPage.helpers';

function stageAllowsActions(state: FmFormFlowStageState | undefined): boolean {
  return state !== 'loading';
}

function FlowActions({
  descriptor,
  interactionsEnabled,
}: {
  descriptor: FmFormFlowPageDescriptor;
  interactionsEnabled: boolean;
}): React.ReactElement | null {
  const surfaceOwned = stateSurfaceActionIds(descriptor);
  const actions = descriptor.actions.filter((action) => !surfaceOwned.has(action.id));
  if (!actions.length) return null;
  // A disabled action says why in words beside it: a pale button with the
  // reason only in a hover title read as broken, and a phone has no hover.
  // A reason the stage already states in one of its blocks is said once there.
  const stated = new Set(activeBlocks(descriptor).map((block) => block.description));
  const held = interactionsEnabled
    ? actions.find(
        (action) => action.disabled && action.disabledReason && !stated.has(action.disabledReason),
      )
    : undefined;
  const reasonId = held ? `${descriptor.id}-action-reason` : undefined;
  return (
    <div className="fm-form-flow__actions" data-testid="form-flow-actions">
      <FmInline gap="sm" justify="end" wrap>
        {held ? (
          <FmText
            id={reasonId}
            size="caption"
            tone="muted"
            className="fm-form-flow__action-reason"
            data-testid="form-flow-action-reason"
          >
            {held.disabledReason}
          </FmText>
        ) : null}
        {actions.map((action) => {
          const disabled = action.disabled || !interactionsEnabled;
          const button = (
            <FmButton
              key={action.id}
              variant={action.variant}
              disabled={disabled}
              aria-describedby={action === held ? reasonId : undefined}
              title={action.disabledReason ?? (!interactionsEnabled ? activeSectionItem(descriptor)?.stateLabel ?? descriptor.stateLabel : undefined)}
              onClick={action.confirmation ? undefined : () => descriptor.onEvent?.({ kind: 'action', actionId: action.id })}
              data-testid={`form-flow-action-${action.id}`}
            >
              {action.label}
            </FmButton>
          );
          if (!action.confirmation) return button;
          return (
            <FmConfirm
              key={action.id}
              title={action.confirmation.title}
              description={action.confirmation.description}
              affectedEntities={action.confirmation.affectedEntities}
              confirmText={action.confirmation.confirmLabel}
              cancelText={action.confirmation.cancelLabel}
              onConfirm={() => descriptor.onEvent?.({ kind: 'action', actionId: action.id })}
              disabled={disabled}
              contentTestId={`form-flow-action-${action.id}-confirmation`}
            >
              {button}
            </FmConfirm>
          );
        })}
      </FmInline>
    </div>
  );
}

/**
 * A PAID boundary keeps its full-fidelity stage content on screen: the lock is a
 * preview, not a wall, so the owner can see what the package unlocks. The signal
 * is the unlock preview itself, not the `locked` state — a capability lock with
 * nothing to buy (a probe that is not armed, a dialog whose precondition is
 * unmet) has no preview to offer and keeps hiding its content, exactly as it did
 * before. Loading, empty and error have nothing served to show at all.
 */
function flowShowsContent(descriptor: FmFormFlowPageDescriptor): boolean {
  if (descriptor.state === 'ready') return true;
  return descriptor.state === 'locked' && descriptor.locked.unlock !== undefined;
}

/**
 * The ONE predicate for "is there stage content on screen". The heading and the
 * block region must agree: gating them separately is how a heading stranded
 * itself over an empty band in the first place.
 */
function flowContentVisible(
  descriptor: FmFormFlowPageDescriptor,
  sectionItem: FmFormFlowSectionItemDescriptor | undefined,
  linearItem: FmFormFlowLinearItemDescriptor | undefined,
): boolean {
  const sectionVisible = linearItem !== undefined
    || sectionStateAllowsInteraction(sectionItem?.state);
  return flowShowsContent(descriptor) && sectionVisible;
}

function flowAllowsControls(
  descriptor: FmFormFlowPageDescriptor,
  sectionItem: FmFormFlowSectionItemDescriptor | undefined,
  linearItem: FmFormFlowLinearItemDescriptor | undefined,
): boolean {
  return descriptor.state === 'ready'
    && sectionStateAllowsInteraction(sectionItem?.state)
    && stageAllowsControls(linearItem?.story?.state);
}

function flowAllowsActions(
  descriptor: FmFormFlowPageDescriptor,
  sectionItem: FmFormFlowSectionItemDescriptor | undefined,
  linearItem: FmFormFlowLinearItemDescriptor | undefined,
): boolean {
  return descriptor.state === 'ready'
    && sectionStateAllowsInteraction(sectionItem?.state)
    && stageAllowsActions(linearItem?.story?.state);
}

function FlowBlockRegion({
  descriptor,
  sectionItem,
  linearItem,
  controlsEnabled,
  showHeaders,
  stageNotice,
}: {
  descriptor: FmFormFlowPageDescriptor;
  sectionItem: FmFormFlowSectionItemDescriptor | undefined;
  linearItem: FmFormFlowLinearItemDescriptor | undefined;
  controlsEnabled: boolean;
  showHeaders?: boolean;
  stageNotice?: React.ReactNode;
}): React.ReactElement | null {
  if (!flowContentVisible(descriptor, sectionItem, linearItem)) return null;
  const blocks = activeBlocks(descriptor);
  const layout = blocks.length > 1 && blocks.some(({ control }) => control.type === 'map')
    ? 'spatial'
    : 'stack';
  return (
    <FmFormFlowBlocks
      blocks={blocks}
      descriptor={descriptor}
      dominantBeatId={linearItem?.story?.dominantBeat}
      showHeaders={showHeaders}
      layout={layout}
      notice={stageNotice}
      noticeBlockId={stageNoticeBlockId(linearItem)}
      controlsEnabled={controlsEnabled}
      busy={linearItem?.story?.state === 'loading'}
      /* A stage-level lock previews its blocks exactly as a flow-level one does,
         so the marker has to say so on both paths (review round 6). */
      previewBehindLock={descriptor.state === 'locked' || linearItem?.story?.state === 'locked'}
    />
  );
}

function FlowSummary({ descriptor }: { descriptor: FmFormFlowPageDescriptor }) {
  if (!descriptor.summary) return null;
  return (
    <aside
      className="fm-form-flow__summary"
      aria-label={descriptor.summary.title}
      data-testid="form-flow-summary"
    >
      <FmText size="caption" weight="semibold" tone="muted">
        {descriptor.summary.title}
      </FmText>
      <Values values={descriptor.summary.values} />
    </aside>
  );
}

export interface FmFormFlowSurfaceProps {
  descriptor: FmFormFlowPageDescriptor;
  presentation?: 'page' | 'dialog';
  'data-testid'?: string;
}

function FlowIntro({
  descriptor,
  presentation,
}: Pick<FmFormFlowSurfaceProps, 'descriptor' | 'presentation'>) {
  if (presentation === 'page') return <FlowHeader descriptor={descriptor} />;
  return descriptor.description
    ? <FmText tone="muted">{descriptor.description}</FmText>
    : null;
}

function FlowActiveContent({
  descriptor,
  active,
  sectionItem,
  linearItem,
  controlsEnabled,
  actionsEnabled,
  compactDialog,
}: {
  descriptor: FmFormFlowPageDescriptor;
  active: ReturnType<typeof activeItem>;
  sectionItem: FmFormFlowSectionItemDescriptor | undefined;
  linearItem: FmFormFlowLinearItemDescriptor | undefined;
  controlsEnabled: boolean;
  actionsEnabled: boolean;
  compactDialog: boolean;
}) {
  const showHeaders = !(
    compactDialog && activeBlocks(descriptor).length === 1
  );
  if (linearItem) {
    const notice = descriptor.state === 'ready'
      ? <LinearStageStateSurface descriptor={descriptor} item={linearItem} />
      : null;
    const anchored = stageNoticeBlockId(linearItem) !== undefined;
    return (
      <section
        className="fm-form-flow__dialog-stage"
        aria-label={linearItem.story.primaryAction}
      >
        {anchored ? null : notice}
        <FlowBlockRegion
          descriptor={descriptor}
          sectionItem={sectionItem}
          linearItem={linearItem}
          controlsEnabled={controlsEnabled}
          showHeaders={showHeaders}
          stageNotice={anchored ? notice : undefined}
        />
        <FlowActions
          descriptor={descriptor}
          interactionsEnabled={actionsEnabled}
        />
      </section>
    );
  }
  return (
    <>
      {/* The heading names the content under it. With nothing served there is
          nothing under it, and it stranded itself below the state surface. A
          one-section dialog is already named by its own title, so the section
          label would print the same thing twice. */}
      {active && !compactDialog && flowContentVisible(descriptor, sectionItem, linearItem)
        ? <ActiveFlowHeading title={active.label} id={`form-flow-heading-${active.id}`} />
        : null}
      {descriptor.state === 'ready'
        ? <SectionStateSurface descriptor={descriptor} />
        : null}
      <FlowBlockRegion
        descriptor={descriptor}
        sectionItem={sectionItem}
        linearItem={linearItem}
        controlsEnabled={controlsEnabled}
        showHeaders={showHeaders}
      />
      <FlowActions descriptor={descriptor} interactionsEnabled={actionsEnabled} />
    </>
  );
}

/** Shared governed form-flow body used by the page root and map-owned dialogs. */
export const FmFormFlowSurface: React.FC<FmFormFlowSurfaceProps> = ({
  descriptor,
  presentation = 'page',
  'data-testid': testId = 'fm-form-flow-surface',
}) => {
  assertFmFormFlowPageDescriptor(descriptor);
  const active = activeItem(descriptor);
  const sectionItem = activeSectionItem(descriptor);
  const linearItem = descriptor.navigation.kind === 'linear' ? active as FmFormFlowLinearItemDescriptor | undefined : undefined;
  const controlsEnabled = flowAllowsControls(descriptor, sectionItem, linearItem);
  const actionsEnabled = flowAllowsActions(descriptor, sectionItem, linearItem);
  const compactDialog = presentation === 'dialog' && descriptor.navigation.items.length === 1;
  const showNavigation = presentation === 'page' || descriptor.navigation.items.length > 1;
  const stageNotice = linearItem && descriptor.state === 'ready'
    ? <LinearStageStateSurface descriptor={descriptor} item={linearItem} />
    : null;
  const headingRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const body = headingRef.current;
    const heading = body?.querySelector<HTMLElement>('h2');
    if (presentation === 'dialog') {
      body?.closest<HTMLElement>('.fm-modal__body')?.scrollTo({ top: 0 });
      body?.closest<HTMLElement>('.fm-modal__root')?.scrollTo({ top: 0, left: 0 });
      heading?.focus({ preventScroll: true });
      return;
    }
    heading?.focus();
    /* `descriptor.state` is a dependency because content is now mounted per
       state: a retry (error -> ready) or an unlock (locked -> ready) remounts
       the stage heading WITHOUT changing the active navigation id, and focus
       must follow the content that just appeared. */
  }, [descriptor.navigation.activeId, descriptor.state, presentation]);
  return (
    <section
      className="fm-form-flow"
      aria-label={descriptor.ariaLabel}
      data-navigation={descriptor.navigation.kind}
      data-presentation={presentation}
      data-testid={testId}
    >
          <FlowIntro descriptor={descriptor} presentation={presentation} />
          {showNavigation ? <LinearNavigation descriptor={descriptor} /> : null}
          {showNavigation ? <SectionNavigation descriptor={descriptor} /> : null}
          <FlowSummary descriptor={descriptor} />
          <div className="fm-form-flow__body" ref={headingRef}>
            <StateSurface descriptor={descriptor} />
            {linearItem && presentation === 'page' ? (
              <StoryTransitionFrame
                as="section"
                className="fm-form-flow__transition"
                kind={linearItem.story.kind}
                conversionRole={linearItem.story.conversionRole}
                copy={<LinearStageStory item={linearItem} />}
                capabilityClassName="fm-form-flow__capability"
                ariaLabel={linearItem.story.primaryAction}
                capability={<>
                  {stageNotice && stageNoticeBlockId(linearItem) === undefined ? stageNotice : null}
                  <FlowBlockRegion
                    descriptor={descriptor}
                    sectionItem={sectionItem}
                    linearItem={linearItem}
                    controlsEnabled={controlsEnabled}
                    stageNotice={stageNoticeBlockId(linearItem) === undefined ? undefined : stageNotice}
                  />
                  <FlowActions descriptor={descriptor} interactionsEnabled={actionsEnabled} />
                </>}
              />
            ) : (
              <FlowActiveContent
                descriptor={descriptor}
                active={active}
                sectionItem={sectionItem}
                linearItem={linearItem}
                controlsEnabled={controlsEnabled}
                actionsEnabled={actionsEnabled}
                compactDialog={compactDialog}
              />
            )}
          </div>
    </section>
  );
};

export const FmFormFlowPage: React.FC<FmFormFlowPageProps> = ({ descriptor, 'data-testid': testId = 'fm-form-flow-page' }) => (
  <FmRoutePage as="main" width="flush" surface="transparent" fit="content" data-navigation={descriptor.navigation.kind} data-testid={testId}>
    <FmContentWidth width="content" data-testid="form-flow-content-rail">
      <FmFormFlowSurface descriptor={descriptor} presentation="page" />
    </FmContentWidth>
  </FmRoutePage>
);

FmFormFlowSurface.displayName = 'FmFormFlowSurface';
FmFormFlowPage.displayName = 'FmFormFlowPage';

export default FmFormFlowPage;
